import type { ThreadApiMessage } from '../types/thread-api.js';
import type { MessageAttachment } from './types.js';
import { classifyMediaUrl, extractUrlsFromText } from './classify-url.js';
import { guessFileNameFromUrl } from './file-kind.js';
import { parseVideoEmbedUrl } from './video-embed.js';

function isHttpUrl(text: string): boolean {
  return /^https?:\/\//i.test(text.trim());
}

function payloadUrl(message: ThreadApiMessage): string {
  const payload = message.raw_message?.payload?.trim();
  if (payload) return payload;

  const body = message.message?.trim() ?? '';
  if (!body) return '';

  // Media rows often store the CDN URL in `message` when `raw_message` is absent.
  if (message.type >= 2 && message.type <= 5) {
    return body;
  }

  if (isHttpUrl(body)) {
    return body;
  }

  const urls = extractUrlsFromText(body);
  if (urls.length === 1) {
    return urls[0]!;
  }

  return '';
}

/** Map Laravel thread message → optional `MessageAttachment`. */
export function mapThreadMessageAttachment(
  message: ThreadApiMessage,
): MessageAttachment | undefined {
  const rawType = (message.raw_message?.type ?? '').toLowerCase();
  const url = payloadUrl(message);
  if (!url) return undefined;

  if (rawType === 'image_url' || message.type === 2) {
    return { kind: 'image', url, fileName: guessFileNameFromUrl(url) };
  }

  if (message.type === 3) {
    const embed = parseVideoEmbedUrl(url);
    if (embed) {
      return {
        kind: 'video_embed',
        url: embed.watchUrl,
        embedUrl: embed.embedUrl,
        provider: embed.provider,
      };
    }
    return { kind: 'video', url, fileName: guessFileNameFromUrl(url) };
  }

  if (message.type === 4) {
    return { kind: 'file', url, fileName: guessFileNameFromUrl(url) };
  }

  if (message.type === 5) {
    return { kind: 'audio', url, fileName: guessFileNameFromUrl(url) };
  }

  if (rawType === 'video_url' || rawType === 'video') {
    const embed = parseVideoEmbedUrl(url);
    if (embed) {
      return {
        kind: 'video_embed',
        url: embed.watchUrl,
        embedUrl: embed.embedUrl,
        provider: embed.provider,
      };
    }
    return { kind: 'video', url, fileName: guessFileNameFromUrl(url) };
  }

  if (
    rawType === 'audio_url' ||
    rawType === 'audio' ||
    rawType === 'voice' ||
    rawType === 'voice_url'
  ) {
    return { kind: 'audio', url, fileName: guessFileNameFromUrl(url) };
  }

  if (
    rawType === 'file' ||
    rawType === 'document' ||
    rawType === 'file_url' ||
    rawType === 'attachment'
  ) {
    return { kind: 'file', url, fileName: guessFileNameFromUrl(url) };
  }

  const classified = classifyMediaUrl(url);
  if (!classified) return undefined;

  if (classified === 'video_embed') {
    const embed = parseVideoEmbedUrl(url);
    if (!embed) return undefined;
    return {
      kind: 'video_embed',
      url: embed.watchUrl,
      embedUrl: embed.embedUrl,
      provider: embed.provider,
    };
  }

  return {
    kind: classified,
    url,
    fileName: guessFileNameFromUrl(url),
  };
}
